import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, MapPin, X, Filter } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useI18n } from "../lib/i18n";
import { API } from "../lib/auth";
import JobCard from "../components/JobCard";
import AlertSubscribe from "../components/AlertSubscribe";
import { CONTRACT_TYPES as CONTRACTS, SECTORS } from "../lib/constants";
import { CATEGORIES } from "../lib/seoTaxonomy";
import { useSeo } from "../lib/useSeo";

export default function JobsPage() {
  const { t, lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [k, setK] = useState(params.get("k") || "");
  const [l, setL] = useState(params.get("l") || "");

  // Build SEO based on the current search query so /jobs?k=hospitality has its own title/description
  const query = params.get("k") || "";
  const loc = params.get("l") || "";
  const seoTitle = lang === "fr"
    ? `${query ? `Emplois "${query}"` : "Toutes les offres d'emploi"}${loc ? ` à ${loc}` : ""} — Motravay`
    : `${query ? `${query} jobs` : "All job listings"}${loc ? ` in ${loc}` : ""} — Motravay`;
  const seoDescription = lang === "fr"
    ? `Toutes les offres d'emploi${query ? ` correspondant à "${query}"` : ""}${loc ? ` à ${loc}` : ""} à Maurice. Postulez en ligne avec ou sans CV.`
    : `All job openings${query ? ` matching "${query}"` : ""}${loc ? ` in ${loc}` : ""} in Mauritius. Apply online with or without CV.`;
  useSeo({
    title: seoTitle,
    description: seoDescription,
    canonical: "/jobs",
  });
  const [contract, setContract] = useState(params.get("contract_type") || "");
  const [sector, setSector] = useState(params.get("sector") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [loading, setLoading] = useState(true);
  const [categoryCounts, setCategoryCounts] = useState({}); // { slug: count }

  const fetchCategoryCounts = async () => {
    try {
      const r = await axios.get(`${API}/jobs/categories`);
      const map = {};
      (r.data.categories || []).forEach(c => { map[c.slug] = c.count; });
      setCategoryCounts(map);
    } catch (_e) { /* non-fatal */ }
  };

  useEffect(() => { fetchCategoryCounts(); }, []);

  const fetchJobs = async (overrides = {}) => {
    setLoading(true);
    const q = new URLSearchParams();
    const _k = overrides.k ?? k;
    const _l = overrides.l ?? l;
    const _contract = overrides.contract ?? contract;
    const _sector = overrides.sector ?? sector;
    const _category = overrides.category ?? category;
    if (_k) q.set("k", _k);
    if (_l) q.set("l", _l);
    if (_contract) q.set("contract_type", _contract);
    if (_sector) q.set("sector", _sector);
    if (_category) q.set("category", _category);
    const r = await axios.get(`${API}/jobs?${q.toString()}`);
    setJobs(r.data.jobs || []);
    setTotal(r.data.total || 0);
    setLoading(false);
  };

  useEffect(() => { fetchJobs(); /* eslint-disable-next-line */ }, []);

  const onSearch = (e) => {
    e?.preventDefault();
    const q = new URLSearchParams();
    if (k) q.set("k", k);
    if (l) q.set("l", l);
    if (contract) q.set("contract_type", contract);
    if (sector) q.set("sector", sector);
    if (category) q.set("category", category);
    setParams(q);
    fetchJobs();
  };

  const clear = () => {
    setK(""); setL(""); setContract(""); setSector(""); setCategory("");
    setParams({});
    fetchJobs({ k: "", l: "", contract: "", sector: "", category: "" });
  };

  const applyCategory = (slug) => {
    const next = slug === category ? "" : slug;
    setCategory(next);
    const q = new URLSearchParams(params);
    if (next) q.set("category", next); else q.delete("category");
    setParams(q);
    fetchJobs({ category: next });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <form onSubmit={onSearch} data-testid="jobs-search-form" className="bg-white border border-slate-200 rounded-2xl shadow-sm p-2 flex flex-col sm:flex-row gap-2 mb-8">
        <div className="flex-1 flex items-center gap-2 px-3">
          <Search className="h-5 w-5 text-slate-400 shrink-0" />
          <Input data-testid="jobs-keyword" value={k} onChange={(e) => setK(e.target.value)} placeholder={t("hero.search")} className="border-0 focus-visible:ring-0 shadow-none h-12" />
        </div>
        <div className="hidden sm:block w-px bg-slate-200" />
        <div className="flex-1 flex items-center gap-2 px-3">
          <MapPin className="h-5 w-5 text-slate-400 shrink-0" />
          <Input data-testid="jobs-location" value={l} onChange={(e) => setL(e.target.value)} placeholder={t("hero.location")} className="border-0 focus-visible:ring-0 shadow-none h-12" />
        </div>
        <Button type="submit" data-testid="jobs-search-submit" className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white h-12 px-6 rounded-xl">{t("hero.cta")}</Button>
      </form>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        <aside className="md:col-span-3" data-testid="jobs-filters">
          <div className="sticky top-24 bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold text-slate-900 inline-flex items-center gap-2"><Filter className="h-4 w-4" /> {t("jobs.filters")}</h3>
              <button type="button" onClick={clear} data-testid="filters-clear" className="text-xs text-slate-500 hover:text-[#1A8FE3] inline-flex items-center gap-1"><X className="h-3 w-3" />{t("jobs.clear")}</button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-medium">{t("jobs.contract")}</label>
                <Select value={contract || "all"} onValueChange={(v) => { setContract(v === "all" ? "" : v); }}>
                  <SelectTrigger data-testid="filter-contract" className="mt-2"><SelectValue placeholder={t("jobs.all")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("jobs.all")}</SelectItem>
                    {CONTRACTS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-medium">{t("jobs.sector")}</label>
                <Select value={sector || "all"} onValueChange={(v) => { setSector(v === "all" ? "" : v); }}>
                  <SelectTrigger data-testid="filter-sector" className="mt-2"><SelectValue placeholder={t("jobs.all")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("jobs.all")}</SelectItem>
                    {SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={onSearch} data-testid="filters-apply" className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full">{t("hero.cta")}</Button>
            </div>
          </div>
        </aside>

        <section className="md:col-span-9" data-testid="jobs-results">
          {/* Category quick-filter chips — only rendered for categories that actually have jobs.
              If the currently-active chip has 0 jobs (e.g. because the last matching job was closed),
              we still render it so the user can see the filter is active and can toggle it off. */}
          {(() => {
            const chipList = CATEGORIES.filter(c => (categoryCounts[c.slug] || 0) > 0 || c.slug === category);
            if (chipList.length === 0) return null;
            return (
              <div className="mb-4 flex flex-wrap gap-2" data-testid="jobs-category-chips">
                {chipList.map(c => {
                  const active = category === c.slug;
                  const count = categoryCounts[c.slug] || 0;
                  return (
                    <button
                      key={c.slug}
                      type="button"
                      onClick={() => applyCategory(c.slug)}
                      data-testid={`chip-category-${c.slug}`}
                      className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${active ? "bg-[#1A8FE3] text-white border-[#1A8FE3]" : "bg-white text-slate-700 border-slate-200 hover:border-[#1A8FE3] hover:text-[#1A8FE3]"}`}
                    >
                      {lang === "fr" ? c.label_fr : c.label_en}
                      <span className={`ml-1.5 text-[10px] font-medium ${active ? "text-white/80" : "text-slate-400"}`}>{count}</span>
                    </button>
                  );
                })}
              </div>
            );
          })()}
          <div className="mb-4 text-sm text-slate-500">{total} {t("jobs.results")}</div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[...Array(6)].map((_, i) => <div key={i} className="h-40 rounded-xl shimmer" />)}
            </div>
          ) : jobs.length === 0 ? (
            <div className="space-y-6">
              <div className="text-center py-12 text-slate-500" data-testid="jobs-empty">{t("jobs.noResults")}</div>
              <AlertSubscribe keyword={k} location={l} />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {jobs.map(j => <JobCard key={j.id} job={j} />)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
